import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

const INPUT_HEIGHT = "48px";
const INPUT_BORDER_RADIUS = "4px";
const INPUT_BORDER_WIDTH = "1px";
const INPUT_PADDING = "12px 8px 12px 16px";
const FIELD_GAP = "10px";

const sectionTitleStyles = {
  fontFamily: "Noto Sans Hebrew",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "140%",
  letterSpacing: 0,
  textAlign: "right" as const,
  textTransform: "capitalize" as const,
};

const cardTitleStyles = {
  fontFamily: "Noto Sans Hebrew",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "140%",
  letterSpacing: 0,
  textAlign: "right" as const,
  textTransform: "capitalize" as const,
};

const fieldLabelStyles = {
  fontFamily: "Noto Sans Hebrew",
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "140%",
  letterSpacing: 0,
  textAlign: "right" as const,
};

const fieldInputValueStyles = (theme: Theme) => ({
  fontFamily: "Noto Sans Hebrew",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "140%",
  letterSpacing: 0,
  textAlign: "right" as const,
  color: theme.palette.secondary.main,
});

const outlinedInputRootStyles = (theme: Theme) => ({
  height: INPUT_HEIGHT,
  borderRadius: INPUT_BORDER_RADIUS,
  backgroundColor: theme.palette.common.white,
  alignItems: "center",
  opacity: 1,
  boxSizing: "border-box" as const,
  ...fieldInputValueStyles(theme),
  "& fieldset": {
    borderColor: theme.palette.divider,
    borderWidth: INPUT_BORDER_WIDTH,
  },
  "&:hover fieldset": {
    borderColor: theme.palette.divider,
    borderWidth: INPUT_BORDER_WIDTH,
  },
  "&.Mui-focused fieldset": {
    borderColor: theme.palette.primary.main,
    borderWidth: INPUT_BORDER_WIDTH,
  },
});

export const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "100%",
    padding: "16px",
    borderRadius: "4px",
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    boxSizing: "border-box",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "100%",
  },
  contactCard: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "100%",
    padding: "16px",
    borderRadius: "4px",
    border: `1px solid ${theme.palette.divider}`,
    boxSizing: "border-box",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    direction: "ltr",
    width: "100%",
    minHeight: "36px",
  },
  sectionTitle: {
    ...sectionTitleStyles,
    color: theme.palette.secondary.main,
  },
  cardTitle: {
    ...cardTitleStyles,
    color: theme.palette.secondary.main,
  },
  iconButton: {
    width: "36px !important",
    height: "36px !important",
    padding: "0 !important",
    color: `${theme.palette.text.primary} !important`,
    borderRadius: "8px !important",
    border: "1px solid transparent",
    boxSizing: "border-box",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: "transparent",
      borderColor: theme.palette.text.primary,
    },
  },
  iconButtonOpen: {
    backgroundColor: "transparent !important",
    borderColor: `${theme.palette.text.primary} !important`,
  },
  menuPaper: {
    width: "129px !important",
    minWidth: "129px !important",
    maxWidth: "129px !important",
    height: "112px !important",
    borderRadius: "4px !important",
    border: `1px solid ${theme.palette.divider}`,
    paddingTop: "4px !important",
    paddingBottom: "4px !important",
    boxSizing: "border-box",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08) !important",
    overflow: "hidden",
    "& .MuiList-root": {
      padding: "0 !important",
      height: "100%",
      boxSizing: "border-box",
    },
  },
  menuItem: {
    "&&": {
      display: "flex",
      flexDirection: "row",
      direction: "ltr",
      justifyContent: "flex-start",
      alignItems: "center",
      gap: "8px",
      width: "100%",
      minHeight: "52px",
      height: "52px",
      padding: "0 12px",
      fontFamily: "Noto Sans Hebrew",
      fontSize: "14px",
      lineHeight: "21px",
      color: theme.palette.text.primary,
    },
  },
  menuItemLabel: {
    flex: 1,
    textAlign: "right",
  },
  menuItemIcon: {
    display: "inline-flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
  },
  fieldsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: FIELD_GAP,
    width: "100%",
    direction: "rtl",
  },
  contactFieldsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: FIELD_GAP,
    width: "100%",
    direction: "rtl",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
    width: "100%",
    direction: "rtl",
  },
  label: {
    ...fieldLabelStyles,
    color: theme.palette.text.primary,
  },
  input: {
    "& .MuiOutlinedInput-root": outlinedInputRootStyles(theme),
    "& .MuiOutlinedInput-input": {
      padding: INPUT_PADDING,
      boxSizing: "border-box",
      height: "100%",
      verticalAlign: "middle",
      ...fieldInputValueStyles(theme),
    },
  },
  phoneInput: {
    "& .MuiOutlinedInput-root": {
      ...outlinedInputRootStyles(theme),
      direction: "ltr",
    },
    "& .MuiOutlinedInput-input": {
      padding: INPUT_PADDING,
      boxSizing: "border-box",
      height: "100%",
      direction: "ltr",
      verticalAlign: "middle",
      ...fieldInputValueStyles(theme),
    },
  },
  formControl: {
    width: "100%",
    "& .MuiOutlinedInput-root": outlinedInputRootStyles(theme),
  },
  select: {
    height: INPUT_HEIGHT,
    borderRadius: INPUT_BORDER_RADIUS,
    backgroundColor: theme.palette.common.white,
    opacity: 1,
    boxSizing: "border-box",
    ...fieldInputValueStyles(theme),
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
      borderWidth: INPUT_BORDER_WIDTH,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
      borderWidth: INPUT_BORDER_WIDTH,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: INPUT_BORDER_WIDTH,
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      minHeight: "unset !important",
      height: "100% !important",
      padding: `${INPUT_PADDING} !important`,
      boxSizing: "border-box",
      verticalAlign: "middle",
      ...fieldInputValueStyles(theme),
    },
  },
  selectValue: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    width: "100%",
    direction: "rtl",
  },
  selectChevron: {
    fontSize: "20px !important",
    color: theme.palette.text.primary,
    flexShrink: 0,
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "66px",
    height: "26px",
    padding: "8px 14px",
    borderRadius: "4px",
    boxSizing: "border-box",
    opacity: 1,
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: "18px",
    color: theme.palette.common.white,
    fontFamily: "Noto Sans Hebrew",
    whiteSpace: "nowrap",
  },
  statusDraft: {
    backgroundColor: theme.palette.customerStatus.draft,
  },
  statusActive: {
    backgroundColor: theme.palette.customerStatus.active,
  },
  statusInactive: {
    backgroundColor: theme.palette.customerStatus.inactive,
  },
  contactsSection: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "100%",
  },
  contactsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    minHeight: "42px",
    direction: "ltr",
  },
  contactsTitle: {
    ...sectionTitleStyles,
    color: theme.palette.secondary.main,
  },
  addButton: {
    height: "42px !important",
    minHeight: "42px !important",
    borderRadius: "4px !important",
    boxShadow: "none !important",
    textTransform: "none !important",
    fontFamily: `${theme.typography.fontFamily} !important`,
    fontSize: "14px !important",
    fontWeight: "600 !important",
    lineHeight: "21px !important",
    backgroundColor: `${theme.palette.primary.main} !important`,
    padding: "10px 22px !important",
    opacity: 1,
    display: "inline-flex !important",
    alignItems: "center !important",
    gap: "6px !important",
    boxSizing: "border-box",
    "& .MuiButton-startIcon": {
      margin: "0 !important",
    },
    "&:hover": {
      boxShadow: "none",
    },
  },
  contactsList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "100%",
  },
}));
